import { useEffect, useState, useCallback } from 'react';
import { Workbox } from 'workbox-window';

export interface VersionNotifierState {
  // Service worker states
  hasUpdate: boolean;
  isUpdating: boolean;
  isVersionReady: boolean; // New version is ready to be activated by user

  // PWA installation states
  canInstall: boolean;
  isInstalling: boolean;
  isInstalled: boolean;

  // App readiness
  isReady: boolean;
}

// Aggregated state interface
export interface AggregatedVersionState {
  serviceWorker: {
    hasUpdate: boolean;
    isUpdating: boolean;
    isVersionReady: boolean;
  };
  pwa: {
    canInstall: boolean;
    isInstalling: boolean;
    isInstalled: boolean;
  };
  app: {
    isReady: boolean;
  };
}

export interface VersionNotifierActions {
  reloadForUpdate: () => void;
  installApp: () => void;
  dismissUpdate: () => void;
  reloadApp: () => void; // New action to reload the app when version is ready
  debugInstallState: () => void;
}

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// Extend Navigator interface for iOS standalone detection
interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

// Early-capture PWA events that might fire before React effects run
type EarlyGlobal = {
  __vnEarlyPwaInit?: boolean;
  __vnEarlyDeferredPrompt?: BeforeInstallPromptEvent | null;
  __vnEarlyInstalled?: boolean;
};

(() => {
  if (typeof window === 'undefined') return;
  const g = globalThis as EarlyGlobal;
  if (g.__vnEarlyPwaInit) return;
  g.__vnEarlyPwaInit = true;

  window.addEventListener('beforeinstallprompt', (event: Event) => {
    const e = event as BeforeInstallPromptEvent;
    try {
      e.preventDefault();
    } catch {
      // Silently handle preventDefault errors
    }
    // Stash for later pickup by the hook
    g.__vnEarlyDeferredPrompt = e;
  });

  window.addEventListener('appinstalled', () => {
    g.__vnEarlyInstalled = true;
    g.__vnEarlyDeferredPrompt = null;
  });
})();

export function useVersionNotifier(): VersionNotifierState &
  VersionNotifierActions {
  // ---------- Shared store to keep multiple hook users in sync ----------
  const INSTALLED_KEY = 'pwa_installed_v1';

  type SharedStore = {
    state: AggregatedVersionState;
    waitingWorker: ServiceWorker | null;
    deferredPrompt: BeforeInstallPromptEvent | null;
    subscribers: Set<(s: AggregatedVersionState) => void>;
  };

  // Store global type
  type StoreGlobal = {
    __vnStore?: SharedStore;
    __vnSwInit?: boolean;
    __vnPwaInit?: boolean;
  } & EarlyGlobal;

  // Module-scoped singletons
  const g = globalThis as StoreGlobal;

  if (!g.__vnStore) {
    g.__vnStore = {
      state: {
        serviceWorker: {
          hasUpdate: false,
          isUpdating: false,
          isVersionReady: false,
        },
        pwa: { canInstall: false, isInstalling: false, isInstalled: false },
        app: { isReady: false },
      },
      waitingWorker: null,
      deferredPrompt: null,
      subscribers: new Set(),
    } as SharedStore;
  }

  const shared = g.__vnStore as SharedStore;

  const setSharedState = (
    updater: (prev: AggregatedVersionState) => AggregatedVersionState
  ) => {
    shared.state = updater(shared.state);
    // Notify subscribers
    for (const cb of shared.subscribers) cb(shared.state);
  };

  // Local state mirrors shared state
  const [state, setState] = useState<AggregatedVersionState>(shared.state);
  useEffect(() => {
    const cb = (s: AggregatedVersionState) => setState(s);
    shared.subscribers.add(cb);
    // Initialize immediately
    setState(shared.state);

    // Force a state refresh after mount to catch any missed events
    setTimeout(() => {
      // Check for early captured events
      const gg = globalThis as EarlyGlobal;
      if (gg.__vnEarlyDeferredPrompt && !shared.state.pwa.isInstalled) {
        shared.deferredPrompt = gg.__vnEarlyDeferredPrompt;
        setSharedState(prev => ({
          ...prev,
          pwa: { ...prev.pwa, canInstall: true },
        }));
      }
    }, 200);

    return () => {
      shared.subscribers.delete(cb);
    };
    // Dependencies intentionally omitted - shared is module singleton
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize workbox and service worker listeners
  useEffect(() => {
    // Ensure we only register SW + listeners once
    if ('serviceWorker' in navigator && !g.__vnSwInit) {
      g.__vnSwInit = true;
      const wb = new Workbox('/sw.js');

      // Service worker events
      wb.addEventListener('installed', event => {
        if (event.isUpdate) {
          // New version installed, waiting to take control
        } else {
          // Service worker installed for the first time
          setSharedState(prev => ({
            ...prev,
            app: { ...prev.app, isReady: true },
          }));
        }
      });

      wb.addEventListener('waiting', event => {
        // New version waiting to take control
        shared.waitingWorker = event.sw || null;
        setSharedState(prev => ({
          ...prev,
          serviceWorker: { ...prev.serviceWorker, hasUpdate: true },
        }));
      });

      wb.addEventListener('controlling', () => {
        // New version is now controlling the page - let user decide when to reload
        setSharedState(prev => ({
          ...prev,
          serviceWorker: {
            hasUpdate: false,
            isUpdating: false,
            isVersionReady: true,
          },
        }));
        // Do NOT automatically reload - wait for user action
      });

      wb.addEventListener('activated', event => {
        if (event.isUpdate) {
          // New version activated
        }
        setSharedState(prev => ({
          ...prev,
          app: { ...prev.app, isReady: true },
        }));
      });

      // Listen for custom refresh events from registerSW
      const handleNeedRefresh = () => {
        setSharedState(prev => ({
          ...prev,
          serviceWorker: { ...prev.serviceWorker, hasUpdate: true },
        }));
      };

      window.addEventListener('sw-need-refresh', handleNeedRefresh);

      // Cleanup function for service worker
      const cleanupSW = () => {
        window.removeEventListener('sw-need-refresh', handleNeedRefresh);
      };

      // Register the service worker
      wb.register()
        .then(registration => {
          // Check if service worker is already controlling the page
          if (
            registration &&
            registration.active &&
            navigator.serviceWorker.controller
          ) {
            // Service worker is already active and controlling the page
            setSharedState(prev => ({
              ...prev,
              app: { ...prev.app, isReady: true },
            }));
          }

          // Check for updates more aggressively after reload
          if (registration && registration.waiting) {
            shared.waitingWorker = registration.waiting;
            setSharedState(prev => ({
              ...prev,
              serviceWorker: { ...prev.serviceWorker, hasUpdate: true },
            }));
          }

          // Force update check after a delay
          const updateTimeout = setTimeout(() => {
            registration?.update();
          }, 2000);

          // Store timeout for cleanup
          Object.assign(cleanupSW, { updateTimeout });
        })
        .catch(() => {
          // Service worker registration failed
          setSharedState(prev => ({
            ...prev,
            app: { ...prev.app, isReady: true },
          }));
        });
      // Return cleanup function
      return cleanupSW;
    } else if (!('serviceWorker' in navigator)) {
      // Service worker not supported or Workbox not available
      // Still set ready to true so the UI doesn't show loading indefinitely
      setSharedState(prev => ({
        ...prev,
        app: { ...prev.app, isReady: true },
      }));
    }
    // Dependencies intentionally omitted - g, shared, setSharedState are module singletons
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // PWA installation listeners with improved status detection
  useEffect(() => {
    if (g.__vnPwaInit) {
      // Still perform a quick initial status sync for this subscriber
      const isStandalone = window.matchMedia(
        '(display-mode: standalone)'
      ).matches;
      const isFullscreen = window.matchMedia(
        '(display-mode: fullscreen)'
      ).matches;
      const isIOSStandalone =
        (window.navigator as NavigatorWithStandalone).standalone === true;
      // Decide installed ONLY by actual runtime context (no persisted flags)
      const isActuallyInstalled = Boolean(
        isStandalone || isFullscreen || isIOSStandalone
      );
      setSharedState(prev => ({
        ...prev,
        pwa: { ...prev.pwa, isInstalled: isActuallyInstalled },
      }));
      // If an early beforeinstallprompt was captured and we're not installed, expose install
      const gg = globalThis as EarlyGlobal;
      if (!isActuallyInstalled && gg.__vnEarlyDeferredPrompt) {
        shared.deferredPrompt =
          gg.__vnEarlyDeferredPrompt as BeforeInstallPromptEvent;
        setSharedState(prev => ({
          ...prev,
          pwa: { ...prev.pwa, canInstall: true },
        }));
      }
      return;
    }
    g.__vnPwaInit = true;
    // Enhanced installation status detection with multiple checks
    const checkInstallStatus = () => {
      // Determine if running as an installed PWA window (runtime only)
      const isStandalone = window.matchMedia(
        '(display-mode: standalone)'
      ).matches;
      const isFullscreen = window.matchMedia(
        '(display-mode: fullscreen)'
      ).matches;
      const isIOSStandalone =
        (window.navigator as NavigatorWithStandalone).standalone === true;

      // Consider installed ONLY when actually running outside the browser UI
      const isActuallyInstalled = Boolean(
        isStandalone || isFullscreen || isIOSStandalone
      );

      setSharedState(prev => ({
        ...prev,
        pwa: { ...prev.pwa, isInstalled: isActuallyInstalled },
      }));

      if (isActuallyInstalled) {
        // If we are in the installed app window, no need to show install prompt
        setSharedState(prev => ({
          ...prev,
          pwa: { ...prev.pwa, canInstall: false },
        }));
        shared.deferredPrompt = null;
      }

      return isActuallyInstalled;
    };

    // Initial check with multiple attempts to ensure reliability
    const performInitialCheck = () => {
      const installed = checkInstallStatus();
      // Pick up any early beforeinstallprompt event captured before mount
      const gg = globalThis as EarlyGlobal;
      if (!installed && gg.__vnEarlyDeferredPrompt) {
        shared.deferredPrompt =
          gg.__vnEarlyDeferredPrompt as BeforeInstallPromptEvent;
        setSharedState(prev => ({
          ...prev,
          pwa: { ...prev.pwa, canInstall: true },
        }));
      }
    };

    // Multiple checks to ensure we catch the state properly after reload
    const timeouts = [
      setTimeout(performInitialCheck, 100),
      setTimeout(performInitialCheck, 500),
      setTimeout(performInitialCheck, 1000),
    ];

    // Listen for display mode changes (important for reload detection)
    const standaloneMedia = window.matchMedia('(display-mode: standalone)');
    const minimalUiMedia = window.matchMedia('(display-mode: minimal-ui)');

    const handleDisplayModeChange = () => {
      setTimeout(checkInstallStatus, 50);
    };

    // Add listeners for both media queries (using modern API)
    standaloneMedia.addEventListener('change', handleDisplayModeChange);
    minimalUiMedia.addEventListener('change', handleDisplayModeChange);

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (event: BeforeInstallPromptEvent) => {
      try {
        event.preventDefault();
      } catch {
        // Silently handle preventDefault errors
      }
      // Only set install prompt if app is not already installed
      const currentlyInstalled = checkInstallStatus();
      if (!currentlyInstalled) {
        shared.deferredPrompt = event;
        setSharedState(prev => ({
          ...prev,
          pwa: { ...prev.pwa, canInstall: true },
        }));
        // Store the event globally as backup
        (globalThis as EarlyGlobal).__vnEarlyDeferredPrompt = event;
      }
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      try {
        localStorage.setItem(INSTALLED_KEY, '1');
      } catch {
        // Silently handle localStorage errors (e.g., in private browsing)
      }
      setSharedState(prev => ({
        ...prev,
        pwa: { canInstall: false, isInstalling: false, isInstalled: true },
      }));
      shared.deferredPrompt = null;
    };

    // Event handlers with timeout tracking
    const eventTimeouts: NodeJS.Timeout[] = [];

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        eventTimeouts.push(setTimeout(checkInstallStatus, 200));
      }
    };

    const handleFocus = () => {
      eventTimeouts.push(setTimeout(checkInstallStatus, 100));
    };

    const handleLoad = () => {
      eventTimeouts.push(setTimeout(checkInstallStatus, 300));
    };

    const handlePopstate = () => {
      eventTimeouts.push(setTimeout(checkInstallStatus, 100));
    };

    const handleHashChange = () => {
      eventTimeouts.push(setTimeout(checkInstallStatus, 100));
    };

    // More frequent status checks to catch state changes, especially after reload
    const intervalCheck = setInterval(() => {
      checkInstallStatus();
      // Also check for deferred prompt availability
      const gg = globalThis as EarlyGlobal;
      if (
        !shared.state.pwa.isInstalled &&
        !shared.state.pwa.canInstall &&
        gg.__vnEarlyDeferredPrompt
      ) {
        shared.deferredPrompt = gg.__vnEarlyDeferredPrompt;
        setSharedState(prev => ({
          ...prev,
          pwa: { ...prev.pwa, canInstall: true },
        }));
      }
    }, 3000);

    /* maybe added early */ if (!(globalThis as EarlyGlobal).__vnEarlyPwaInit) {
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    }
    /* maybe added early */ if (!(globalThis as EarlyGlobal).__vnEarlyPwaInit) {
      window.addEventListener('appinstalled', handleAppInstalled);
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('load', handleLoad);
    window.addEventListener('popstate', handlePopstate);
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      standaloneMedia.removeEventListener('change', handleDisplayModeChange);
      minimalUiMedia.removeEventListener('change', handleDisplayModeChange);
      clearInterval(intervalCheck);
      timeouts.forEach(clearTimeout);
      eventTimeouts.forEach(clearTimeout);
      if (!(globalThis as EarlyGlobal).__vnEarlyPwaInit) {
        window.removeEventListener(
          'beforeinstallprompt',
          handleBeforeInstallPrompt
        );
        window.removeEventListener('appinstalled', handleAppInstalled);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('load', handleLoad);
      window.removeEventListener('popstate', handlePopstate);
      window.removeEventListener('hashchange', handleHashChange);
    };
    // Dependencies intentionally omitted - g, shared, setSharedState are module singletons
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Actions
  const reloadForUpdate = useCallback(() => {
    const updateSW = window.__updateSW;
    if (updateSW) {
      setSharedState(prev => ({
        ...prev,
        serviceWorker: { ...prev.serviceWorker, isUpdating: true },
      }));
      // Use the updateSW function from registerSW
      updateSW(true);
    } else if (shared.waitingWorker) {
      setSharedState(prev => ({
        ...prev,
        serviceWorker: { ...prev.serviceWorker, isUpdating: true },
      }));
      // Fallback: Send SKIP_WAITING message to activate the waiting service worker
      shared.waitingWorker.postMessage({ type: 'SKIP_WAITING' });

      // Listen for the controlling event to know when to show version ready
      navigator.serviceWorker.addEventListener(
        'controllerchange',
        () => {
          setSharedState(prev => ({
            ...prev,
            serviceWorker: {
              hasUpdate: false,
              isUpdating: false,
              isVersionReady: true,
            },
          }));
        },
        { once: true }
      );
    }
    // Dependencies intentionally omitted - shared and setSharedState are module singletons
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const installApp = useCallback(async () => {
    if (!shared.deferredPrompt) {
      return;
    }

    setSharedState(prev => ({
      ...prev,
      pwa: { ...prev.pwa, isInstalling: true },
    }));

    try {
      await shared.deferredPrompt.prompt();
      const choiceResult = await shared.deferredPrompt.userChoice;

      if (choiceResult.outcome === 'accepted') {
        // User accepted the install prompt
        // The 'appinstalled' event will handle the state update
      } else {
        // User dismissed the install prompt
        setSharedState(prev => ({
          ...prev,
          pwa: { ...prev.pwa, canInstall: false, isInstalling: false },
        }));
      }
    } catch {
      // Error during app installation
      setSharedState(prev => ({
        ...prev,
        pwa: { ...prev.pwa, isInstalling: false },
      }));
    } finally {
      shared.deferredPrompt = null;
    }
    // Dependencies intentionally omitted - shared and setSharedState are module singletons
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismissUpdate = useCallback(() => {
    setSharedState(prev => ({
      ...prev,
      serviceWorker: { ...prev.serviceWorker, hasUpdate: false },
    }));
    // Dependencies intentionally omitted - setSharedState is module singleton
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reloadApp = useCallback(() => {
    // Clear the version ready state and reload the application
    setSharedState(prev => ({
      ...prev,
      serviceWorker: { ...prev.serviceWorker, isVersionReady: false },
    }));
    // Small delay to ensure state is updated before reload
    setTimeout(() => {
      window.location.reload();
    }, 100);
    // Dependencies intentionally omitted - setSharedState is module singleton
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const debugInstallState = useCallback(() => {
    // Development mode only debugging - function kept for compatibility
  }, []);

  return {
    // States - extract from aggregated state
    hasUpdate: state.serviceWorker.hasUpdate,
    isUpdating: state.serviceWorker.isUpdating,
    isVersionReady: state.serviceWorker.isVersionReady,
    canInstall: state.pwa.canInstall,
    isInstalling: state.pwa.isInstalling,
    isInstalled: state.pwa.isInstalled,
    isReady: state.app.isReady,

    // Actions
    reloadForUpdate,
    installApp,
    dismissUpdate,
    reloadApp,
    debugInstallState,
  };
}
